import { useMemo } from "react";

/**
 * Extract Asset Name from symbol
 * Rules:
 * - Must be MCX, NFO, or BFO
 * - EXCLUDE: NIFTY, BANKNIFTY, FINNIFTY, MIDCPNIFTY, SENSEX, BANKEX
 * - Returns the asset name (e.g., CRUDEOIL, GOLD, RELIANCE)
 */
function getAssetFromSymbol(symbol) {
    try {
        if (!symbol) return null;
        const s = symbol.toUpperCase();

        // 1. Strict Prefix Enforcement
        // Must explicitly contain MCX, NFO, or BFO, or end with FUT
        const isFUT = s.endsWith("FUT");
        if (!s.includes("MCX") && !s.includes("NFO") && !s.includes("BFO") && !isFUT) return null;

        // 4. Remove prefixes to get clean symbol
        let cleanSymbol = s.replace("MCX:", "").replace("NFO:", "").replace("BFO:", "").replace("NSE:", "").replace("BSE:", "");

        // 5. Check against Known Commodities (Always Allow)
        const knownCommodities = [
            "CRUDEOIL", "GOLD", "SILVER", "NATURALGAS", "COPPER", "ZINC", "LEAD", "ALUMINIUM", "MENTHAOIL", "COTTON"
        ];

        for (const asset of knownCommodities) {
            if (cleanSymbol.startsWith(asset)) return asset;
        }

        // 6. Check for Derivatives (Digits present)
        // Already handled above in Step 3.

        // 3. EXCLUDE Major Indices - REMOVED per user request
        // Now we include EVERYTHING from NFO/BFO/MCX
        // const excluded = ["NIFTY", "BANKNIFTY", "FINNIFTY", "MIDCPNIFTY", "SENSEX", "BANKEX"];
        // for (const ex of excluded) {
        //     if (cleanSymbol.startsWith(ex)) {
        //         return null;
        //     }
        // }

        // 7. Extract Asset for allowed symbols
        // Known stocks? We just fall back to regex for them.
        const knownAssets = [
            "RELIANCE", "HDFCBANK", "INFY", "TCS", "SBIN"
        ];

        for (const asset of knownAssets) {
            if (cleanSymbol.startsWith(asset)) return asset;
        }

        // Fallback: Capture letters before first digit
        const match = cleanSymbol.match(/^([A-Z]+)/);
        return match ? match[1] : null;

    } catch (e) {
        console.error("Error extracting asset from symbol:", symbol, e);
        return null;
    }
}

/**
 * Calculate MODE (most frequent value)
 */
function calculateMode(numbers) {
    if (!numbers || numbers.length === 0) return 0;
    const frequency = {};
    let maxFreq = 0, mode = numbers[0];
    numbers.forEach(num => {
        frequency[num] = (frequency[num] || 0) + 1;
        if (frequency[num] > maxFreq) { maxFreq = frequency[num]; mode = num; }
    });
    return mode;
}

/**
 * Get Multiplier
 * Tries to find specific multiplier (e.g. crudeoil_multiplier), else defaults to 1.
 */
function getMultiplier(userMappings, clientId, asset) {
    if (!userMappings || !clientId || !asset) return 1;
    const mapping = userMappings.find((u) => u.client_id === clientId);
    if (!mapping) return 1;

    const key = `${asset.toLowerCase()}_multiplier`;
    const val = Number(mapping[key]);
    return val > 0 ? val : 1;
}

/**
 * Main Hook
 */
export function usePledgePlusOrdersView(orders, userMappings, positions) {
    return useMemo(() => {
        if (!orders || orders.length === 0) {
            return { data: [], baseQtyMap: {}, activeAssets: [] };
        }

        // ------------------------------------------------------
        // 0. Pre-Process Positions to find "Covered" Quantities & Detailed Map
        // ------------------------------------------------------
        const clientAssetPositionMap = {}; // { clientId: { ASSET: { longQty: 0, shortQty: 0 } } }
        const detailedPositionMap = {};    // { clientId_Symbol_Strategy: { netQty: 0 } }

        if (positions && positions.length > 0) {
            positions.forEach(pos => {
                const clientId = pos.clientId || pos.client_id;
                if (!clientId) return;

                // detailed map for SL check - Normalize Key
                const strat = (pos.strategy || "").toString().trim().toUpperCase() || "NO_STRATEGY";
                const cleanSym = (pos.symbol || "").toString().trim().toUpperCase().replace(/^(MCX|NSE|NFO|BFO|CDS):/i, "");

                const key = `${clientId}_${cleanSym}_${strat}`;
                detailedPositionMap[key] = {
                    netQty: pos.net_qty || pos.qty || 0,
                    symbol: pos.symbol,
                    strategy: strat
                };

                // Get Asset
                const asset = getAssetFromSymbol(pos.symbol);
                if (!asset) return;

                // Net Qty
                const netQty = pos.net_qty || pos.qty || 0;
                if (Math.abs(netQty) === 0) return;

                if (!clientAssetPositionMap[clientId]) clientAssetPositionMap[clientId] = {};
                if (!clientAssetPositionMap[clientId][asset]) {
                    clientAssetPositionMap[clientId][asset] = { longQty: 0, shortQty: 0 };
                }

                if (netQty > 0) clientAssetPositionMap[clientId][asset].longQty += netQty;
                if (netQty < 0) clientAssetPositionMap[clientId][asset].shortQty += Math.abs(netQty);
            });

            // DEBUG: Print keys for CA00246
            const debugKeys = Object.keys(detailedPositionMap).filter(k => k.includes("CA00246"));

        }

        // ------------------------------------------------------
        // 1. Filter & Group Buy Orders by Client + Asset
        // ------------------------------------------------------
        const clientAssetMap = {}; // { clientId: { ASSET: { qty: 0, orders: [] } } }
        const allAssetQtys = {};   // { ASSET: [normalizedQtys...] } - for base qty calc

        orders.forEach(order => {
            // FILTER: Valid Status (4 or 6) as per OrdersView logic
            // Assuming 4=Traded?, 6=Pending? Verify if "Buy Order View" shows pending or traded.
            // OrdersView.js uses: String(o.status_no) === "6" || String(o.status_no) === "4";
            const validStatus = String(order.status_no) === "6" || String(order.status_no) === "4";
            if (!validStatus) return;

            // Determine Side
            const sideStr = String(order.side || "").toLowerCase();
            const isBuy = sideStr === "1" || sideStr === "buy";
            const isSell = sideStr === "-1" || sideStr === "2" || sideStr === "sell";

            if (!isBuy && !isSell) return;

            // FILTER: Exclude SL Orders (Pending + Opposite to Position)
            const isPending = String(order.status_no) === "6";
            if (isPending) {
                const clientId = order.clientId || order.client_id;

                const strategy = (order.strategy || "").toString().trim().toUpperCase() || "NO_STRATEGY";
                const cleanSym = (order.symbol || "").toString().trim().toUpperCase().replace(/^(MCX|NSE|NFO|BFO|CDS):/i, "");

                const posKey = `${clientId}_${cleanSym}_${strategy}`;

                const matchingPos = detailedPositionMap[posKey];

                if (matchingPos) {
                    const netQty = matchingPos.netQty;
                    const isLong = netQty > 0;
                    const isShort = netQty < 0;

                    if ((isLong && isSell) || (isShort && isBuy)) {
                        return; // SKIP SL ODER
                    }
                }
            }

            // FILTER: Valid Asset
            const asset = getAssetFromSymbol(order.symbol);
            if (!asset) return;

            const clientId = order.clientId || order.client_id;
            const qty = Math.abs(parseFloat(order.qty || 0));

            // Init Client Map
            if (!clientAssetMap[clientId]) clientAssetMap[clientId] = {};
            if (!clientAssetMap[clientId][asset]) {
                clientAssetMap[clientId][asset] = {
                    maxQty: 0,
                    buyQty: 0,
                    sellQty: 0,
                    rawBuyQty: 0,
                    rawSellQty: 0,
                    orders: []
                };
            }

            // FILTERING LOGIC: Checked Covered vs Excess
            // REVERTED: User found "0 Count" confusing (thought orders were missing).
            // Now we show FULL Qty (Gross Orders) regardless of position coverage.
            let displayQty = qty;

            /* 
            const posData = clientAssetPositionMap[clientId]?.[asset];
            if (posData) {
                if (isBuy && posData.shortQty > 0) {
                    const covered = Math.min(qty, posData.shortQty);
                    displayQty = Math.max(0, qty - covered);
                    posData.shortQty = Math.max(0, posData.shortQty - covered); 
                } else if (isSell && posData.longQty > 0) {
                    const covered = Math.min(qty, posData.longQty);
                    displayQty = Math.max(0, qty - covered);
                    posData.longQty = Math.max(0, posData.longQty - covered);
                }
            }
            */

            // Aggregate ONLY Display/Excess Qty
            if (displayQty > 0) {
                if (isBuy) clientAssetMap[clientId][asset].buyQty += displayQty;
                if (isSell) clientAssetMap[clientId][asset].sellQty += displayQty;
            }

            // Aggregate RAW Qty
            if (isBuy) clientAssetMap[clientId][asset].rawBuyQty = (clientAssetMap[clientId][asset].rawBuyQty || 0) + qty;
            if (isSell) clientAssetMap[clientId][asset].rawSellQty = (clientAssetMap[clientId][asset].rawSellQty || 0) + qty;

            // Update MaxQty based on RAW quantities (preserves sorting logic)
            clientAssetMap[clientId][asset].maxQty = Math.max(
                clientAssetMap[clientId][asset].rawBuyQty,
                clientAssetMap[clientId][asset].rawSellQty
            );


            clientAssetMap[clientId][asset].orders.push(order);
        });

        // ------------------------------------------------------
        // 2. Base Qty Calculation
        // ------------------------------------------------------
        // We need to normalize by multiplier first.
        // Since we are iterating clients now, let's build the rows and collect normalized qtys simultaneously.
        // Actually, to get true base qty mode, we need all normalized qtys across all clients.

        // First pass: Collect raw quantities per asset/client to normalize later
        // ... handled in step 1.

        // Now iterate clients to build data and collect normalized qtys
        const tempRows = [];
        const activeAssetsSet = new Set();

        Object.keys(clientAssetMap).forEach(clientId => {
            // Find User Mapping
            let userMapping = null;
            if (userMappings) {
                const matches = userMappings.filter((u) => u.client_id === clientId);
                // Prioritize 'dhan_xts' or just take first
                userMapping = matches.find(u => (u.broker || "").toLowerCase().includes("dhan")) || matches[0];
            }

            const row = {
                clientId,
                clientName: userMapping ? userMapping.name : `Client ${clientId}`,
                broker: userMapping ? userMapping.broker : "unknown",
            };

            Object.keys(clientAssetMap[clientId]).forEach(asset => {
                activeAssetsSet.add(asset);
                const assetData = clientAssetMap[clientId][asset];
                const mult = getMultiplier(userMappings, clientId, asset);

                // Collect for Base Qty Calc
                // Normalize using maxQty (dominant side)
                const normalized = assetData.maxQty / mult;
                if (!allAssetQtys[asset]) allAssetQtys[asset] = [];
                allAssetQtys[asset].push(normalized);

                row[asset] = {
                    multiplier: mult,
                    buyQty: assetData.buyQty,
                    sellQty: assetData.sellQty,
                    qty: assetData.maxQty, // Keep for sorting/legacy
                    // expectedQty & color will be set in 2nd pass after BaseQty is known
                };
            });

            tempRows.push(row);
        });

        // Calculate Base Qty (Mode)
        const baseQtyMap = {};
        Object.keys(allAssetQtys).forEach(asset => {
            baseQtyMap[asset] = calculateMode(allAssetQtys[asset]);
        });

        // ------------------------------------------------------
        // 3. Final Pass: Validation Colors
        // ------------------------------------------------------
        const activeAssets = Array.from(activeAssetsSet).sort();
        const data = tempRows.map(row => {
            activeAssets.forEach(asset => {
                if (row[asset]) {
                    const cell = row[asset];
                    const base = baseQtyMap[asset] || 0;
                    // If multiplier is 1 (default) and base is huge, it might be correct,
                    // but usually we expect Multiplier * Base = Qty
                    const expected = cell.multiplier * base;

                    cell.expectedQty = expected;

                    // Logic: Match if EITHER BuyQty OR SellQty matches expected
                    // Or if both match?
                    // Usually user has one side. 
                    // If BuyQty > 0 and SellQty == 0 -> Check Buy
                    // If SellQty > 0 and BuyQty == 0 -> Check Sell
                    // If Both -> Check Both? 

                    let isMatch = false;
                    if (cell.buyQty > 0 && cell.sellQty === 0) isMatch = (cell.buyQty === expected);
                    else if (cell.sellQty > 0 && cell.buyQty === 0) isMatch = (cell.sellQty === expected);
                    else if (cell.buyQty > 0 && cell.sellQty > 0) isMatch = (cell.buyQty === expected || cell.sellQty === expected); // Loose match?

                    cell.match = isMatch;
                    cell.color = isMatch ? "green" : "red";
                }
            });
            return row;
        });

        // ------------------------------------------------------
        // 4. Calculate Symbol+Strategy Base Qty (for Modal)
        // ------------------------------------------------------
        // We need { "SYMBOL_STRATEGY": { index: "ASSET", baseQty: MODE, clients: COUNT } }

        const symbolStrategyData = {}; // { KEY: { quantities: [], symbol, strategy, asset } }

        orders.forEach(order => {
            // Re-use logic: Valid Status, Valid Asset
            const validStatus = String(order.status_no) === "6" || String(order.status_no) === "4";
            if (!validStatus) return;
            const asset = getAssetFromSymbol(order.symbol);
            if (!asset) return;

            const sideStr = String(order.side || "").toLowerCase();
            const isBuy = sideStr === "1" || sideStr === "buy";
            const isSell = sideStr === "-1" || sideStr === "2" || sideStr === "sell";
            if (!isBuy && !isSell) return;

            // FILTER: Exclude SL Orders Logic (Same as above)
            const isPending = String(order.status_no) === "6";
            if (isPending) {
                const clientId = order.clientId || order.client_id;

                const strategy = (order.strategy || "").toString().trim().toUpperCase() || "NO_STRATEGY";
                const cleanSym = (order.symbol || "").toString().trim().toUpperCase().replace(/^(MCX|NSE|NFO|BFO|CDS):/i, "");

                const posKey = `${clientId}_${cleanSym}_${strategy}`;

                const matchingPos = detailedPositionMap[posKey];

                if (matchingPos) {
                    const netQty = matchingPos.netQty;
                    const isLong = netQty > 0;
                    const isShort = netQty < 0;

                    if ((isLong && isSell) || (isShort && isBuy)) {
                        return; // SKIP SL ORDER
                    }
                }
            }



            const clientId = order.clientId || order.client_id;
            const qty = Math.abs(parseFloat(order.qty || 0));
            const strategy = order.strategy || "NO_STRATEGY";
            const key = `${order.symbol}_${strategy}`;

            if (!symbolStrategyData[key]) {
                symbolStrategyData[key] = {
                    symbol: order.symbol,
                    strategy,
                    asset,
                    clientQuantities: {} // { clientId: maxQty }
                };
            }

            if (!symbolStrategyData[key].clientQuantities[clientId]) {
                symbolStrategyData[key].clientQuantities[clientId] = 0;
            }
            // For symbol strategy base qty, we check if this order adds to the existing logic
            // But wait, are we summing ALL orders for a client? 
            // In the previous logic we just summed everything.
            // Let's stick to summing everything per client per symbol which creates a "Net" or "Total"
            // Actually, for Base Qty calc, we usually want the Position Size.
            // If a client has Buy 50 and Sell 50 (square off), their Position is 0?
            // But these are ORDERS view.
            // Let's just Max(Buy, Sell) for now to estimate intended position size?
            // Or just Sum everything? 
            // Previous code summed `qty` for all buy orders.
            // Let's Sum `qty` but we need to know if we are aggregating buy or sell?
            // Let's aggregate Max(Buy, Sell) per client per symbol.

            // To do that, we need an intermediate step.
            // Let's simplify: Just calculate Mode of ALL orders for now or stick to the "Max of Buy/Sell" pattern efficiently.

            if (!symbolStrategyData[key].clientSideQuantities) symbolStrategyData[key].clientSideQuantities = {};
            if (!symbolStrategyData[key].clientSideQuantities[clientId]) symbolStrategyData[key].clientSideQuantities[clientId] = { buy: 0, sell: 0 };

            if (isBuy) symbolStrategyData[key].clientSideQuantities[clientId].buy += qty;
            if (isSell) symbolStrategyData[key].clientSideQuantities[clientId].sell += qty;
        });

        const symbolStrategyBaseQty = {};
        Object.entries(symbolStrategyData).forEach(([key, group]) => {
            const normalizedQtys = [];

            // Iterate clientSideQuantities to find Max(Buy, Sell) per client
            if (group.clientSideQuantities) {
                Object.entries(group.clientSideQuantities).forEach(([clientId, sides]) => {
                    const maxQ = Math.max(sides.buy, sides.sell);
                    const mult = getMultiplier(userMappings, clientId, group.asset);
                    normalizedQtys.push(maxQ / mult);
                });
            } else {
                // Fallback if no clientSideQuantities (shouldn't happen with new logic)
            }

            // Calculate Mode of Normalized Quantities
            const mode = calculateMode(normalizedQtys);

            symbolStrategyBaseQty[key] = {
                symbol: group.symbol,
                strategy: group.strategy,
                index: group.asset, // Use 'index' key to match Modal expectations
                baseQty: mode,
                clients: Object.keys(group.clientSideQuantities || {}).length
            };
        });

        return { data, activeAssets, baseQtyMap, symbolStrategyBaseQty };

    }, [orders, userMappings, positions]);
}
